import { useDialogContext } from '@/components/contexts/ModalContext';
import { CancelButton } from '@/components/formLayouts/CancelButton';
import { FormContainer, ValidationErrors } from '@/components/formLayouts/FormContainer';
import { SubmitButton } from '@/components/formLayouts/SubmitButton';
import { isTotalCostOfOwnershipEnabled } from '@/featureFlags';
import { useHttpMutation } from '@/resources/mutation/useHttpMutation';
import { useSites } from '@/resources/organizationSettings/sitesHandler';
import { useSourcingScenarioSuspense } from '@/resources/sourcingScenario/sourcingScenarioHandlers';
import { useSourcingTemplates } from '@/resources/sourcingTemplate/sourcingTemplateHandler';
import { useSupplierAndStockLocations } from '@/resources/supplierAndStockLocation/supplierAndStockLocationHandler';
import { t } from '@lingui/macro';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tab,
    Tabs,
    usePersistedState,
} from '@luminovo/design-system';
import { Box } from '@mui/material';
import { GeneralTab } from './components/GeneralTab';
import { InventoryPreferencesTab } from './components/InventoryPreferencesTab';
import { SolutionPreferencesTab } from './components/SolutionPreferencesTab';
import { SolutionTagPreferncesTab } from './components/SolutionTagPreferncesTab';
import { SupplierPreferencesTab } from './components/SupplierPreferencesTab';
import { TotalCostOfOwnershipRulesTab } from './components/TotalCostOfOwnershipRulesTab';
import {
    convertSourcingScenarioDTOToFormValues,
    convertSourcingScenarioFormValuesToPatchDTO,
    SourcingScenarioFormValues,
    SourcingScenarioTab,
} from './components/types';

function InnerContent() {
    const [tab, setTab] = usePersistedState<SourcingScenarioTab>('sourcing-scenario-edit-tab', 'solution-preference');

    return (
        <DialogContent sx={{ height: '80vh' }}>
            <Tabs size={'large'} value={tab} onChange={(_, value) => setTab(value)} sx={{ marginBottom: '12px' }}>
                <Tab value={'general'} label={t`General`} />
                <Tab value={'solution-preference'} label={t`Solution preferences`} />
                <Tab value={'solution-tags-preferences'} label={t`Solution tags preferences`} />
                <Tab value={'supplier-preferences'} label={t`Supplier preferences`} />
                <Tab value={'inventory-preferences'} label={t`Inventory preferences`} />
                {isTotalCostOfOwnershipEnabled() && (
                    <Tab value={'total-cost-of-ownership-rules'} label={t`Total cost of ownership rules`} />
                )}
            </Tabs>
            <Box sx={{ overflow: 'auto', marginTop: '12px', height: 'calc(100% - 48px)' }}>
                {tab === 'general' && <GeneralTab />}
                {tab === 'solution-preference' && <SolutionPreferencesTab />}
                {tab === 'solution-tags-preferences' && <SolutionTagPreferncesTab />}
                {tab === 'supplier-preferences' && <SupplierPreferencesTab />}
                {tab === 'inventory-preferences' && <InventoryPreferencesTab />}
                {tab === 'total-cost-of-ownership-rules' && <TotalCostOfOwnershipRulesTab />}
            </Box>
        </DialogContent>
    );
}

export function useSourcingScenarioEditDialog({ sourcingScenarioId }: { sourcingScenarioId: string }) {
    const { setDialog, closeDialog } = useDialogContext();

    const { mutateAsync } = useHttpMutation('PATCH /sourcing-scenarios/:sourcingScenarioId', {
        snackbarMessage: t`Sourcing scenario updated`,
        onSuccess: () => closeDialog(),
    });

    const submitHandler = async (formValues: SourcingScenarioFormValues) => {
        await mutateAsync({
            pathParams: { sourcingScenarioId: sourcingScenarioId },
            requestBody: convertSourcingScenarioFormValuesToPatchDTO(formValues),
        });
    };

    const { data: sourcingScenario } = useSourcingScenarioSuspense(sourcingScenarioId);
    const { data: supplierAndStockLocations } = useSupplierAndStockLocations();
    const { data: inventorySites } = useSites();
    const { data: sourcingTemplates } = useSourcingTemplates();

    const defaultValues = convertSourcingScenarioDTOToFormValues({
        sourcingScenario,
        supplierAndStockLocations,
        inventorySites,
        sourcingTemplates,
    });

    const validationErrors: ValidationErrors<SourcingScenarioFormValues> = {
        'sourcing_scenario.missing_name': {
            fieldPath: 'general.name',
        },
        'sourcing_scenario.empty_assembly_quantities': {
            fieldPath: 'assemblyDemands',
        },
        'total_cost_of_ownership_rule.invalid_name': {
            fieldPath: 'root.serverError',
        },
        'total_cost_of_ownership_rule.conditions_cannot_be_empty': {
            fieldPath: 'root.serverError',
        },
        'total_cost_of_ownership_rule.absolute_cost_cannot_be_negative': {
            fieldPath: 'root.serverError',
        },
    };

    const openDialog = () => {
        setDialog(
            <Dialog open={true} maxWidth="xl" fullWidth={true} onClose={closeDialog}>
                <DialogTitle title={t`Edit sourcing scenario`} onClose={closeDialog} sticky={true} />
                <FormContainer
                    onSubmit={submitHandler}
                    validationErrors={validationErrors}
                    defaultValues={defaultValues}
                >
                    <InnerContent />
                    <DialogActions sticky={true}>
                        <CancelButton onClick={closeDialog} />
                        <SubmitButton />
                    </DialogActions>
                </FormContainer>
            </Dialog>,
        );
    };

    return { openDialog };
}
